import { useEffect, useState } from 'react'
import toast from 'react-hot-toast'
import { ShieldExclamationIcon, IdentificationIcon } from '@heroicons/react/24/outline'
import { complianceAPI } from '../services/api'
import type { CreatorVettingResponse } from '../types'
import { useAuthStore } from '../store/authStore'
import LoadingScreen from '../components/LoadingScreen'

const AdminVetting = () => {
  const { user } = useAuthStore()
  const [loading, setLoading] = useState(true)
  const [applications, setApplications] = useState<CreatorVettingResponse[]>([])
  const [notes, setNotes] = useState<Record<number, string>>({})
  const [actingOn, setActingOn] = useState<number | null>(null)

  useEffect(() => {
    if (user?.is_superuser) {
      loadPending()
    }
  }, [user?.is_superuser])

  const loadPending = async () => {
    try {
      setLoading(true)
      const pending = await complianceAPI.listPendingVetting()
      setApplications(pending)
    } catch (error) {
      console.error('Error loading vetting applications:', error)
      toast.error('Failed to load pending applications')
    } finally {
      setLoading(false)
    }
  }

  const handleReview = async (vettingId: number, approve: boolean) => {
    // The creator sees these notes; a rejection needs them to know what to fix.
    if (!approve && !notes[vettingId]?.trim()) {
      toast.error('Add a note telling the creator why, so they can fix it and resubmit')
      return
    }
    try {
      setActingOn(vettingId)
      await complianceAPI.reviewVetting(vettingId, {
        approve,
        review_notes: notes[vettingId]?.trim() || undefined,
      })
      setApplications((prev) => prev.filter((app) => app.id !== vettingId))
      toast.success(approve ? 'Application approved' : 'Application rejected')
    } catch (error: any) {
      console.error('Error reviewing vetting application:', error)
      toast.error(error?.response?.data?.detail || 'Failed to submit review')
    } finally {
      setActingOn(null)
    }
  }

  if (!user?.is_superuser) {
    return (
      <div className="max-w-md mx-auto text-center py-16">
        <ShieldExclamationIcon className="h-10 w-10 text-danger-500 mx-auto mb-3" />
        <h1 className="text-lg font-semibold text-gray-900">Access denied</h1>
        <p className="text-sm text-gray-500 mt-1">
          You don&apos;t have permission to view this page.
        </p>
      </div>
    )
  }

  if (loading) return <LoadingScreen />

  return (
    <div className="max-w-3xl mx-auto space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Creator Vetting Review</h1>
        <p className="text-sm text-gray-500 mt-1">
          Approve or reject pending creator monetization applications. Approved
          creators can create tiers, marketplace products and signal packages.
        </p>
      </div>

      {applications.length === 0 ? (
        <div className="bg-white rounded-lg shadow p-6 text-center text-sm text-gray-500">
          No pending applications.
        </div>
      ) : (
        applications.map((app) => (
          <section key={app.id} className="bg-white rounded-lg shadow p-6 space-y-4">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div className="flex items-center gap-2">
                <IdentificationIcon className="h-5 w-5 text-primary-600" />
                <h2 className="text-lg font-semibold text-gray-900">{app.legal_name}</h2>
              </div>
              <span className="text-xs text-gray-500">
                User #{app.user_id} · Submitted{' '}
                {app.submitted_at ? new Date(app.submitted_at).toLocaleString() : 'unknown'}
              </span>
            </div>

            <dl className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
              <div>
                <dt className="text-gray-500">Jurisdiction</dt>
                <dd className="text-gray-900">{app.jurisdiction ?? 'N/A'}</dd>
              </div>
              <div>
                <dt className="text-gray-500">Registered advisor</dt>
                <dd className="text-gray-900">{app.is_registered_advisor ? 'Yes' : 'No'}</dd>
              </div>
              <div className="sm:col-span-2">
                <dt className="text-gray-500">Registration details</dt>
                <dd className="text-gray-900">
                  {app.registration_details || (app.is_registered_advisor ? 'Not given' : 'None (not registered)')}
                </dd>
              </div>
              <div className="sm:col-span-2">
                <dt className="text-gray-500">Compensation disclosure</dt>
                <dd className="text-gray-900">{app.compensation_disclosure || 'None disclosed'}</dd>
              </div>
              <div className="sm:col-span-2">
                <dt className="text-gray-500">Accuracy attestation</dt>
                {/* The server refuses an application without it. */}
                <dd className="text-gray-900">Confirmed</dd>
              </div>
            </dl>

            <div>
              <label
                htmlFor={`review-notes-${app.id}`}
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                Review notes (shown to the creator; required to reject)
              </label>
              <textarea
                id={`review-notes-${app.id}`}
                value={notes[app.id ?? 0] ?? ''}
                onChange={(e) =>
                  setNotes((prev) => ({ ...prev, [app.id ?? 0]: e.target.value }))
                }
                rows={2}
                className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
              />
            </div>

            <div className="flex gap-3">
              <button
                type="button"
                onClick={() => app.id && handleReview(app.id, true)}
                disabled={actingOn === app.id}
                className="px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 transition-colors font-medium disabled:opacity-50"
              >
                Approve
              </button>
              <button
                type="button"
                onClick={() => app.id && handleReview(app.id, false)}
                disabled={actingOn === app.id}
                className="px-4 py-2 border border-danger-500 text-danger-500 rounded-md hover:bg-danger-50 transition-colors font-medium disabled:opacity-50"
              >
                Reject
              </button>
            </div>
          </section>
        ))
      )}
    </div>
  )
}

export default AdminVetting
